// Core HTTP wrapper for the app.
// - Reads BASE_URL from Vite env
// - Attaches Authorization header if token is set
// - Parses server error envelope and throws ApiError
// - Provides qs() helper for query strings

import type { ApiErrorEnvelope } from "../interface/common";

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, "") ?? "http://localhost:3000";

export class ApiError extends Error {
  status: number;
  envelope?: ApiErrorEnvelope;
  constructor(message: string, status: number, envelope?: ApiErrorEnvelope) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.envelope = envelope;
  }
}

/** Simple in-memory + localStorage auth token holder */
const AUTH_STORAGE_KEY = "auth.tokens";

type StoredAuth = {
  accessToken: string;
  refreshToken?: string;
  tokenExpires?: number; // backend unit TBD
  userId?: string;
  email?: string;
};

let cachedAuth: StoredAuth | null = null;

export function getAuth(): StoredAuth | null {
  // if (cachedAuth) return cachedAuth;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    cachedAuth = JSON.parse(raw) as StoredAuth;
    return cachedAuth;
  } catch {
    return null;
  }
}

export function setAuth(auth: StoredAuth | null) {
  cachedAuth = auth;
  if (auth) localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  else localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function getAccessToken(): string | null {
  return getAuth()?.accessToken ?? null;
}

/** Build query string: supports arrays by repeating the key */
export function qs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach((item) => sp.append(k, String(item)));
    else sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

type ApiInit = RequestInit & { skipAuth?: boolean };

async function parseMaybeJson(res: Response) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Main fetch wrapper
 * Usage: api<T>("/api/v1/devices/list" + qs({ page: 1 }))
 */
export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE_URL}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };

  // Attach auth header unless explicitly skipped
  if (!init.skipAuth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url, { ...init, headers });

  const payload = await parseMaybeJson(res);

  if (!res.ok) {
    // Handle 401 Unauthorized - always redirect to login
    if (res.status === 401 && !path.includes("/login")) {
      // Clear auth tokens
      setAuth(null);
      
      // Force redirect to login page
      window.location.replace("/login");
      
      // Return a never-resolving promise to prevent the calling code from continuing
      // This stops any error handlers from running before the redirect completes
      return new Promise<T>(() => {});
    }

    // Try to read the documented error envelope
    const env = typeof payload === "object" && payload
      ? (payload as ApiErrorEnvelope)
      : undefined;

    const message =
      env?.message ||
      env?.error ||
      (typeof payload === "string" ? payload : res.statusText);

    throw new ApiError(message || "Request failed", res.status, env);
  }

  return payload as T;
}